import { supabase } from '../supabase'
import { fetchAllPaged, fetchByIdsChunked } from '../pagination'
import { displayDate } from '../../utils/dates'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string) => (supabase as any).from(t)

// The Expense tab is a UNION of two differently-shaped sources that cannot be a
// single SQL query, so both are fetched in full (bounded per period, paginated
// via fetchAllPaged) and merged/sorted/sliced client-side:
//
//   Source 1 — expenses rows: creation-time cash (one_time + prepayment are
//     status='paid'; accruals never are). Void = status='voided'. The creation
//     journal is entry_type IN ('expense','prepayment') (mig 102); the void
//     reversal is a separate 'manual' journal (mig 188), so status is the void
//     signal, not journal.is_voided.
//   Source 2 — accrual settlement journals: entry_type='accrual_payment'
//     (mig 103). No payment_method is captured → Method shows the payment GL
//     account name; Reference is not stored → blank. One journal can settle
//     several accruals → Category/Payee show 'Multiple'. Void = is_voided/is_reversed.

const CREATION_ENTRY_TYPES = ['expense', 'prepayment']
const CASH_EXPENSE_TYPES = ['one_time', 'prepayment']

export interface ExpensePaymentFilters {
  from: string
  to: string
  search: string
  showVoided: boolean
}

export interface ExpensePaymentRow {
  id: string
  kind: 'expense' | 'settlement'
  date: string
  category: string
  payee: string
  method: string | null
  methodIsAccount: boolean   // settlement rows: method is a GL account name, not a real method
  reference: string | null
  amount: number
  typeLabel: 'One-time' | 'Prepayment' | 'Accrual settlement'
  isVoided: boolean
  journalNumber: string | null
  journalId: string | null
}

function sanitizeTerm(term: string): string {
  return term.replace(/[(),]/g, ' ').trim()
}

// ── Source 1: expenses (creation-time cash) ──────────────────────────────────
async function fetchExpenseRows(tid: string, filters: ExpensePaymentFilters): Promise<ExpensePaymentRow[]> {
  const statuses = filters.showVoided ? ['paid', 'voided'] : ['paid']
  const raw = await fetchAllPaged<Record<string, unknown>>((from, to) => {
    let q = db('expenses')
      .select('id, ref_no, amount, expense_date, payment_method, status, expense_type, category_id, expense_vendor_id, supplier_name, expense_categories(name)')
      .eq('tenant_id', tid)
      .in('status', statuses)
      .in('expense_type', CASH_EXPENSE_TYPES)
      .gte('expense_date', filters.from)
      .lte('expense_date', filters.to)
    if (filters.search) {
      const t = sanitizeTerm(filters.search)
      q = q.or(`description.ilike.%${t}%,supplier_name.ilike.%${t}%,ref_no.ilike.%${t}%`)
    }
    return q.order('id', { ascending: true }).range(from, to)
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = raw as any[]
  const expenseIds = rows.map(r => r.id as string)

  // Creation journal (entry_number) per expense.
  const journals = expenseIds.length
    ? await fetchByIdsChunked<{ source_id: string; entry_number: string; id: string }>(expenseIds, batch =>
        db('journal_entries')
          .select('source_id, entry_number, id')
          .eq('tenant_id', tid)
          .eq('source_type', 'expenses')
          .in('entry_type', CREATION_ENTRY_TYPES)
          .in('source_id', batch))
    : []
  const jmap = new Map(journals.map(j => [j.source_id, j]))

  // Vendor name for rows where supplier_name is empty but an expense_vendor is set.
  const vendorIds = [...new Set(rows.filter(r => !r.supplier_name && r.expense_vendor_id).map(r => r.expense_vendor_id as string))]
  const vendors = vendorIds.length
    ? await fetchByIdsChunked<{ id: string; name: string }>(vendorIds, batch =>
        db('expense_vendors').select('id, name').in('id', batch))
    : []
  const vmap = new Map(vendors.map(v => [v.id, v.name]))

  return rows.map(r => ({
    id: r.id,
    kind: 'expense' as const,
    date: r.expense_date,
    category: r.expense_categories?.name ?? '—',
    payee: r.supplier_name ?? (r.expense_vendor_id ? vmap.get(r.expense_vendor_id) ?? '—' : '—'),
    method: r.payment_method ?? null,
    methodIsAccount: false,
    reference: r.ref_no ?? null,
    amount: Number(r.amount ?? 0),
    typeLabel: r.expense_type === 'prepayment' ? ('Prepayment' as const) : ('One-time' as const),
    isVoided: r.status === 'voided',
    journalNumber: jmap.get(r.id)?.entry_number ?? null,
    journalId: jmap.get(r.id)?.id ?? null,
  }))
}

// ── Source 2: accrual settlement journals ────────────────────────────────────
async function fetchSettlementRows(tid: string, filters: ExpensePaymentFilters): Promise<ExpensePaymentRow[]> {
  const journals = await fetchAllPaged<Record<string, unknown>>((from, to) => {
    let q = db('journal_entries')
      .select('id, entry_number, entry_date, source_id, is_voided, is_reversed')
      .eq('tenant_id', tid)
      .eq('source_type', 'expenses')
      .eq('entry_type', 'accrual_payment')
      .gte('entry_date', filters.from)
      .lte('entry_date', filters.to)
    if (!filters.showVoided) q = q.not('is_voided', 'is', true).not('is_reversed', 'is', true)
    return q.order('id', { ascending: true }).range(from, to)
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const jrows = journals as any[]
  if (!jrows.length) return []

  const journalIds = jrows.map(j => j.id as string)
  const lines = await fetchByIdsChunked<Record<string, unknown>>(journalIds, batch =>
    db('journal_entry_lines')
      .select('journal_id, debit_amount, credit_amount, contact_id, contact_name, payment_account_id, account_id, accounts(name)')
      .in('journal_id', batch))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const byJournal = new Map<string, any[]>()
  for (const l of lines as any[]) {
    const arr = byJournal.get(l.journal_id) ?? []
    arr.push(l)
    byJournal.set(l.journal_id, arr)
  }

  // Resolve each journal to its payment line (the cash credit) + accrued lines.
  const prelim = jrows
    .map(j => {
      const ls = byJournal.get(j.id) ?? []
      const paymentLine = ls.find(l => l.payment_account_id != null)
      const accrued = ls.filter(l => l.contact_id != null)
      return { j, paymentLine, accrued }
    })
    .filter(x => x.paymentLine) // pure write-offs move no cash — not a payment

  // Category for single-accrual settlements comes from the (single) source expense.
  const singleSourceIds = [...new Set(
    prelim.filter(x => x.accrued.length <= 1).map(x => x.j.source_id as string).filter(Boolean),
  )]
  const srcExpenses = singleSourceIds.length
    ? await fetchByIdsChunked<{ id: string; expense_categories: { name: string } | null }>(singleSourceIds, batch =>
        db('expenses').select('id, expense_categories(name)').in('id', batch))
    : []
  const catMap = new Map(srcExpenses.map(e => [e.id, e.expense_categories?.name ?? null]))

  return prelim.map(({ j, paymentLine, accrued }) => {
    const multiple = accrued.length > 1
    return {
      id: j.id,
      kind: 'settlement' as const,
      date: j.entry_date,
      category: multiple ? 'Multiple' : (catMap.get(j.source_id) ?? '—'),
      payee: multiple ? 'Multiple' : (accrued[0]?.contact_name ?? '—'),
      method: paymentLine.accounts?.name ?? null,
      methodIsAccount: true,
      reference: null,
      amount: Number(paymentLine.credit_amount ?? 0),
      typeLabel: 'Accrual settlement' as const,
      isVoided: !!j.is_voided || !!j.is_reversed,
      journalNumber: j.entry_number ?? null,
      journalId: j.id,
    }
  })
}

/**
 * Full filtered set of expense payments (both sources), sorted date-desc. The tab
 * derives the page slice, footer total, and export from this one list — a
 * two-source union cannot be server-paginated as a single query.
 */
export async function fetchAllExpensePayments(tid: string, filters: ExpensePaymentFilters): Promise<ExpensePaymentRow[]> {
  const [a, b] = await Promise.all([fetchExpenseRows(tid, filters), fetchSettlementRows(tid, filters)])
  const merged = [...a, ...b]
  merged.sort((x, y) => {
    if (x.date !== y.date) return x.date < y.date ? 1 : -1
    return x.id < y.id ? 1 : -1
  })
  return merged
}

export interface ExpenseExportRow extends Record<string, unknown> {
  date: string
  category: string
  payee: string
  type: string
  method: string
  reference: string
  amount: number
  journal: string
  status: string
}

export function toExpenseExportRow(r: ExpensePaymentRow): ExpenseExportRow {
  return {
    date: displayDate(r.date),
    category: r.category,
    payee: r.payee,
    type: r.typeLabel,
    method: r.method ?? '',
    reference: r.reference ?? '',
    amount: r.amount,
    journal: r.journalNumber ?? r.journalId ?? '',
    status: r.isVoided ? 'Voided' : 'Posted',
  }
}
